import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  FlatList,
  RefreshControl,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../navigation/RootNavigator';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/Ionicons';
import { api } from '../services/api';
import logger from '../utils/logger';

interface UnmappedSensor {
  sensorNumber: string;
  serialNumber: string;
  gatewayName: string;
  gatewaySn: string;
  siteName: string;
  zoneName?: string;
  hasTemperature: boolean;
  hasHumidity: boolean;
  hasBattery: boolean;
  hasSignal: boolean;
  lastSeenAt: string;
  datapoints: {
    temperature?: { value: number; unit: string };
    humidity?: { value: number; unit: string };
    battery?: { value: number; unit: string };
    rssi?: { value: number; unit: string };
  };
}

type UnmappedSensorsListNavigationProp = StackNavigationProp<
  RootStackParamList,
  'UnmappedSensorsList'
>;

export default function UnmappedSensorsListScreen() {
  const navigation = useNavigation<UnmappedSensorsListNavigationProp>();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sensors, setSensors] = useState<UnmappedSensor[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUnmappedSensors();
  }, []);

  const fetchUnmappedSensors = async () => {
    try {
      setLoading(true);
      setError(null);

      logger.log('📋 Fetching unmapped sensors');

      const response = await api.get('/devices/unmapped-sensors');

      if (response.data.success) {
        setSensors(response.data.data);
        logger.log('✅ Found unmapped sensors:', response.data.data.length);
      } else {
        throw new Error(response.data.error?.message || t('failed_to_load'));
      }
    } catch (err: any) {
      logger.error('Failed to fetch unmapped sensors:', err);
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUnmappedSensors();
  };

  const handleAddSensor = (sensor: UnmappedSensor) => {
    navigation.navigate('DeviceConfig', {
      serialNumber: sensor.serialNumber,
      sensor,
      mode: 'list',
    });
  };

  const handleScanBarcode = () => {
    navigation.navigate('ScanBarcode');
  };

  const getRelativeTime = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return t('just_now');
    if (minutes < 60) return `${minutes} ${t('minutes_ago')}`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${t('hours_ago')}`;

    const days = Math.floor(hours / 24);
    return `${days} ${t('days_ago')}`;
  };

  const renderSensorCard = ({ item }: { item: UnmappedSensor }) => (
    <View style={styles.sensorCard}>
      <View style={styles.cardHeader}>
        <View style={styles.sensorInfo}>
          <Text style={styles.sensorNumber}>#{item.sensorNumber}</Text>
          <Text style={styles.sensorType}>
            {item.hasTemperature && item.hasHumidity
              ? t('temp_humidity_sensor')
              : item.hasTemperature
              ? t('temperature_sensor')
              : t('humidity_sensor')}
          </Text>
        </View>
        <TouchableOpacity style={styles.addButton} onPress={() => handleAddSensor(item)}>
          <Icon name="add-circle" size={32} color="#4CAF50" />
        </TouchableOpacity>
      </View>

      <View style={styles.metricsRow}>
        {item.datapoints.temperature && (
          <View style={styles.metricChip}>
            <Text style={styles.metricIcon}>🌡️</Text>
            <Text style={styles.metricValue}>{item.datapoints.temperature.value}°C</Text>
          </View>
        )}
        {item.datapoints.humidity && (
          <View style={styles.metricChip}>
            <Text style={styles.metricIcon}>💧</Text>
            <Text style={styles.metricValue}>{item.datapoints.humidity.value}%</Text>
          </View>
        )}
        {item.datapoints.battery && (
          <View style={styles.metricChip}>
            <Text style={styles.metricIcon}>🔋</Text>
            <Text style={styles.metricValue}>{item.datapoints.battery.value}%</Text>
          </View>
        )}
      </View>

      <View style={styles.cardFooter}>
        <View style={styles.locationInfo}>
          <Icon name="business" size={14} color="#666" />
          <Text style={styles.locationText}>{item.gatewayName}</Text>
        </View>
        <View style={styles.timeInfo}>
          <Icon name="time" size={14} color="#666" />
          <Text style={styles.timeText}>{getRelativeTime(item.lastSeenAt)}</Text>
        </View>
      </View>
    </View>
  );

  if (loading && !refreshing) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#2196F3" />
          <Text style={styles.loadingText}>{t('loading_sensors')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error && !refreshing) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Icon name={isRTL ? 'chevron-forward' : 'chevron-back'} size={28} color="#333" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('unmapped_sensors')}</Text>
          <View style={{ width: 40 }} />
        </View>
        <View style={styles.loadingContainer}>
          <Icon name="alert-circle-outline" size={48} color="#e74c3c" />
          <Text style={[styles.loadingText, { color: '#e74c3c', marginTop: 12 }]}>{error}</Text>
          <TouchableOpacity
            onPress={fetchUnmappedSensors}
            style={{
              marginTop: 16,
              backgroundColor: '#2196F3',
              paddingHorizontal: 24,
              paddingVertical: 10,
              borderRadius: 8,
            }}
          >
            <Text style={{ color: '#fff', fontWeight: '600' }}>
              {isRTL ? 'إعادة المحاولة' : 'Retry'}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name={isRTL ? 'chevron-forward' : 'chevron-back'} size={28} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('unmapped_sensors')}</Text>
        <View style={{ width: 40 }} />
      </View>

      {/* Stats */}
      <View style={styles.statsBar}>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{sensors.length}</Text>
          <Text style={styles.statLabel}>{t('available')}</Text>
        </View>
      </View>

      {/* Sensors List */}
      <FlatList
        data={sensors}
        renderItem={renderSensorCard}
        keyExtractor={(item) => item.serialNumber}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Icon name="sensors" size={64} color="#ccc" />
            <Text style={styles.emptyTitle}>{t('no_unmapped_sensors')}</Text>
            <Text style={styles.emptySubtitle}>{t('all_sensors_assigned')}</Text>
          </View>
        }
      />

      {/* Scan Barcode FAB */}
      <TouchableOpacity style={styles.fab} onPress={handleScanBarcode}>
        <Icon name="barcode" size={28} color="#fff" />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  statsBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  statItem: {
    alignItems: 'center',
    marginRight: 24,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2196F3',
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  listContent: {
    padding: 16,
  },
  sensorCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sensorInfo: {
    flex: 1,
  },
  sensorNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  sensorType: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  addButton: {
    padding: 4,
  },
  metricsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  metricChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
  },
  metricIcon: {
    fontSize: 16,
    marginRight: 4,
  },
  metricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  locationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  locationText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 4,
  },
  timeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 80,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#2196F3',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});
